import { memo } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { Select, Stack, Typography } from '@lyearn/ui';
import { SetFieldValue } from '@/components/Form';
import { getSelectOptionLabel } from '@/components/Form/components/Select/getOptionLabel';
import SwitchWithInfo from '@/components/SwitchWithInfo';
import {
  CompletionSettingsInput,
  EventStatusEnum,
  EventTypeEnum,
  LiveEventProps,
  Maybe,
  RecurringSessionCompletionRollupTypeEnum,
  RecurringTypeEnum,
} from '@/types/schema';

interface Props {
  type: EventTypeEnum;
  value: CompletionSettingsInput;
  liveEventProps?: Maybe<LiveEventProps>;
  setFieldValue: SetFieldValue;
  onChange: (value: CompletionSettingsInput) => void;
  status?: EventStatusEnum;
  recurringCategory: RecurringTypeEnum;
}

const EventCompletionSettings = (props: Props) => {
  const { t } = useTranslation();
  const { liveEventProps, setFieldValue, type, value, onChange, status, recurringCategory } = props;

  let optionsFroRollupType = [
    {
      label: t('Events:Editor.CompletionSettings.RecurringSessionRollupType.Highest'),
      value: RecurringSessionCompletionRollupTypeEnum.Highest,
    },
    {
      label: t('Events:Editor.CompletionSettings.RecurringSessionRollupType.Average'),
      value: RecurringSessionCompletionRollupTypeEnum.Average,
    },
  ];

  const onConsiderRecordingProgressChange = useEvent((considerRecordingProgress: boolean) => {
    if (
      type === EventTypeEnum.Live &&
      considerRecordingProgress &&
      !liveEventProps?.recordingSettings.shareRecording
    ) {
      setFieldValue('liveEventProps.recordingSettings.shareRecording', true);
    }

    onChange({ ...value, considerRecordingProgress });
  });

  const onRequirePreWorkPostWorkChange = useEvent((requirePreWorkPostWork: boolean) => {
    onChange({ ...value, requirePreWorkPostWork });
  });

  const onRecurringSessionCompletionRollupTypeChage = useEvent(
    (recurringSessionCompletionRollupType: RecurringSessionCompletionRollupTypeEnum) => {
      onChange({ ...value, recurringSessionCompletionRollupType });
    },
  );
  return (
    <Stack direction="col" gapY={24}>
      {recurringCategory === RecurringTypeEnum.Recurring && (
        <Stack direction="col" gapY={8}>
          <Typography className="px-8" variant="body-short-02">
            {t('Events:Editor.CompletionSettings.RecurringSessionRollupType.Description')}
          </Typography>
          <Select
            disableClearable
            getOptionLabel={getSelectOptionLabel}
            options={optionsFroRollupType}
            size="large"
            value={value.recurringSessionCompletionRollupType ?? undefined}
            onChange={onRecurringSessionCompletionRollupTypeChage}
          />
        </Stack>
      )}
      <Stack className="px-8" direction="col" gapY={24}>
        <SwitchWithInfo
          checked={Boolean(value.considerRecordingProgress)}
          description={t('Events:Editor.CompletionSettings.RecordingDescription')}
          label={t('Events:Editor.CompletionSettings.Recording')}
          onChange={onConsiderRecordingProgressChange}
        />
        <SwitchWithInfo
          checked={value.requirePreWorkPostWork}
          description={t('Events:Editor.CompletionSettings.PrePostWorkDescription')}
          disabled={status === EventStatusEnum.Completed}
          label={t('Events:Editor.CompletionSettings.PrePostWork')}
          onChange={onRequirePreWorkPostWorkChange}
        />
      </Stack>
    </Stack>
  );
};

export default memo(EventCompletionSettings);
